import React, { useState } from 'react'
import styled from 'styled-components'

import { NavLink as RouterNavLink, NavLinkProps } from 'react-router-dom'
import {
  Grid as MuiGrid,
  IconButton,
  LinearProgress,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core'
import { spacing } from '@material-ui/system'
import { Clear as ClearIcon, Done as DoneIcon, Edit as EditIcon } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

const Paper = styled(MuiPaper)(spacing)
const Grid = styled(MuiGrid)(spacing)

const DetailTableHead = () => (
  <TableHead>
    <TableRow>
      <TableCell colSpan={2} />
    </TableRow>
  </TableHead>
)

const useStyles = makeStyles(() => ({
  iconButton: {
    padding: '0',
  },
  edit: {
    fontSize: '12px',
    cursor: 'pointer',
  },
  save: {
    fontSize: '16px',
    cursor: 'pointer',
    color: 'green',
  },
  cancel: {
    fontSize: '16px',
    cursor: 'pointer',
    color: 'red',
  },
  hidden: {
    display: 'none',
  },
}))

type Alignment = 'left' | 'center' | 'right'

interface Field {
  id: string
  label: string
  editor?: React.FC<{ row: any; value: any }>
  alignment?: Alignment
  renderer: React.FC<{ row: any; value: any }>
}

interface FieldValueProps {
  obj: any
  field: Field
  value: any
}

const FieldValue = (props: FieldValueProps) => {
  const { obj, field, value } = props
  const [hover, setHover] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const classes = useStyles()
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <Grid container spacing={1} alignItems="center" justify="flex-end">
        <Grid item className={editMode ? '' : classes.hidden}>
          <IconButton
            className={classes.iconButton}
            type="submit"
            onClick={() => setEditMode(false)}
          >
            <DoneIcon className={classes.save} />
          </IconButton>
        </Grid>
        <Grid item className={editMode ? '' : classes.hidden}>
          <IconButton
            className={classes.iconButton}
            type="button"
            onClick={() => setEditMode(false)}
          >
            <ClearIcon className={classes.cancel} />
          </IconButton>
        </Grid>
        {!!field.editor && hover && !editMode && (
          <Grid item>
            <IconButton
              className={classes.iconButton}
              type="button"
              onClick={(e) => {
                e.preventDefault()
                setEditMode(true)
              }}
            >
              <EditIcon className={classes.edit} />
            </IconButton>
          </Grid>
        )}
        <Grid item className={editMode ? '' : classes.hidden}>
          {field.editor && <field.editor row={obj} value={value} />}
        </Grid>
        <Grid item className={editMode ? classes.hidden : ''}>
          <field.renderer row={obj} value={value} />
        </Grid>
      </Grid>
    </div>
  )
}

interface ObjInfo {
  data: any
  isLoading: boolean
}

interface DetailTableProps {
  obj: ObjInfo
  fields: Field[]
  customFields?: Record<string, string>
}

export const DetailTable = (props: DetailTableProps) => {
  const { obj, fields, customFields = {} } = props

  return (
    <div>
      <Paper>
        <TableContainer>
          <Table aria-labelledby="tableTitle" size={'small'} aria-label="enhanced table">
            <DetailTableHead />
            <TableBody>
              {obj.isLoading && <LinearProgress />}
              {fields.map((field) => {
                const path = field.id.split('.')
                let value = obj.data
                for (let i = 0; i < path.length; i += 1) {
                  if (!value || !(path[i] in value)) {
                    value = null
                    break
                  }
                  value = value[path[i]]
                }
                return (
                  <TableRow key={field.id} hover>
                    <TableCell component="th" scope="row">
                      {field.label}
                    </TableCell>
                    <TableCell component="th" scope="row" align="right">
                      <FieldValue obj={obj.data} field={field} value={value} />
                    </TableCell>
                  </TableRow>
                )
              })}
              {Object.keys(customFields).map((label) => {
                const value = customFields[label]
                return (
                  <TableRow key={label} hover>
                    <TableCell component="th" scope="row">
                      {label}
                    </TableCell>
                    <TableCell component="th" scope="row" align="right">
                      {value}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  )
}
